/*
  <GameCard
	horizontal            //Boolean, horizontal Card
	description           // Game Description id horizontal axis is true
	to                    // Game ID
	name                  // Game Name
	thumbnail             //Game Thumbnail src
	tags                  // Array of tags ["tag1", "tag2"]
  />
*/
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import Tag from "../Tag";
import { useTheme } from "styled-components";

const CardSkeleton = ({ h, ...rest }) => {
  const theme = useTheme();
  if (h) {
    return (
      <SkeletonTheme color={theme.color.secondaryBg} highlightColor={theme.color.background}>
        <div style={{ display: "flex" }} {...rest}>
          <div>
            <Skeleton width={275} height={170} style={{ marginRight: "10px" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Skeleton width={100} />
            <Skeleton width={150} height={10} />
            <Skeleton width={200} height={10} />
            <Skeleton width={180} height={10} />
            <Skeleton width={130} />
          </div>
        </div>
      </SkeletonTheme>
    );
  } else
    return (
      <SkeletonTheme color={theme.color.secondaryBg} highlightColor={theme.color.background}>
        <div {...rest}>
          <Skeleton width={170} height={200} style={{ marginBottom: "5px" }} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Skeleton width={100} height={14} />
            <Skeleton width={130} height={14} />
          </div>
        </div>
      </SkeletonTheme>
    );
};
// Main component
const GameCard = (props) => {
  const [loading, setLoading] = useState(1);

  // If the game has no image, do not keep skeleton
  useEffect(() => {
    if (!props.thumbnail) setLoading(0);
  }, [props.thumbnail]);

  return (
    <>
      <Card horizontal={props.horizontal} {...props} loading={loading}>
        <Thumbnail
          setLoading={setLoading}
          to={"/game/" + props.to}
          img={props.thumbnail}
          name={props.name}
        />
        <Body>
          <Name to={"/game/" + props.to} name={props.name} />
          {props.horizontal && <p>{props.description}</p>}
          <Tags tags={props.tags} />
        </Body>
      </Card>
      {loading === 1 && <CardSkeleton h={props.horizontal} {...props} />}
    </>
  );
};
export default GameCard;

export const Tags = ({ withGame, tags, gameid }) => {
  return (
    <TagContainer withGame={withGame}>
      {tags?.map((tag, index) => {
        let path = withGame && index === 0 ? "/game/" + gameid : "/tag/" + tag;
        return (
          <Tag key={index} to={path}>
            {tag}
          </Tag>
        );
      })}
    </TagContainer>
  );
};
const Name = ({ to, name }) => (
  <NameStyle to={to}>
    <h6>{name}</h6>
  </NameStyle>
);

const Thumbnail = ({ to, img, name, setLoading }) => (
  <ThumbnailStyle to={to}>
    {img ? (
      <img
        src={img}
        alt={name}
        onLoad={() => setLoading(0)}
        onError={() => setLoading(0)}
      />
    ) : (
      <div style={{ height: "100%", width: "100%", background: "rgba(0,0,0,0.1)" }} />
    )}
  </ThumbnailStyle>
);

const Card = styled.article`
  position: relative;
  width: 170px;
  display: ${({ loading }) => (loading === 1 ? "none" : "block")};

  ${({ horizontal }) =>
    horizontal &&
    `
      display: flex;
      width: 100%;
      & > div:first-child {
		width: 275px;
		height: 170px;
		a{
			width: 100%;
			max-width: 100%;
			height: 100%;
		}
	  }
      & > div:last-child {
		margin-left: 10px;
      }
    `}
`;

const Body = styled.div`
  width: 100%;
  padding: 0 3px;
  overflow: hidden;

  p {
    color: ${({ theme }) => theme.color.lighterText};
    max-width: 437px;
    font-size: 12px;
    margin: 13px 0 15px;
  }
`;

const TagContainer = styled.div`
  padding-top: 5px;
  padding-bottom: 5px;
  width: 100%;
  display: flex;
  align-items: center;
  > *:not(:last-child) {
    overflow: hidden;
    margin-right: 3px;
  }
  ${({ theme, withGame }) =>
    withGame &&
    `
      > *:first-child {
        >*{
          border: 1px solid ${theme.color.primary};
          color: ${theme.color.primary};
          max-width: 120px;
          transition: color 200ms, background-color 200ms;
          :hover{
            transition: color 200ms, background-color 200ms;
            color: ${theme.color.white};
            background-color: ${theme.color.primary};
          }
        }
      }
    `}
`;

const NameStyle = styled(Link)`
  color: ${({ theme }) => theme.color.darkerText};
  ${({ theme }) => theme.linesLimits(1, 1.4)};
  transition: color 100ms;
  :hover {
    transition: color 200ms;
    color: ${({ theme }) => theme.color.primary};
  }
`;

const ThumbnailStyle = styled(Link)`
  display: inline-block;
  height: 237px;
  width: 366px;
  max-width: 170px;
  border-radius: 4px;
  overflow: hidden;

  img {
    border-radius: 4px;
    height: 100%;
    width: 100%;
    object-fit: cover;
    transition: transform 200ms ease;
    :hover {
      transition: transform 200ms ease;
      transform: scale(1.2) rotate(10deg);
    }
  }

  ::before {
    content: "";
    border-radius: 4px;
    overflow: hidden;
    width: calc(100% + 10px);
    height: calc(100% + 10px);
    background-color: ${({ theme }) => theme.color.secondaryBg};
    position: absolute;
    inset: -5px;
    z-index: -1;
    opacity: 0;
    visibility: hidden;
  }
  &:focus {
    ::before {
      opacity: 1;
      visibility: visible;
      transition: background-color 200ms ease;
    }
  }
`;
